import { Component, inject, ViewChild } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

// PrimeNG
import { ButtonModule } from 'primeng/button';

// Composants
import { BaseComponent } from '../../abstract/base.component';
import { DocumentationArticleViewComponent } from '../documentation-article-view/documentation-article-view.component';
import { DocAssistantContext, DocumentationAssistantComponent } from '../documentation-assistant/documentation-assistant.component';
import { DocLinkTarget } from '../documentation-content/documentation-content.component';

// Modèles
import { DocArticle } from '../../../models/documentation/documentation.types';

/**
 * Lecture d'un article **sans quitter l'écran** : c'est la forme que prend la
 * documentation quand elle est appelée depuis un contexte — le « ? » d'un
 * écran, ou l'aide d'un tableau. On lit, on ferme, on reprend son travail là où
 * on l'avait laissé.
 *
 * Les renvois d'un article ouvrent l'article suivant **dans la fenêtre**. Le
 * bouton « Ouvrir dans la documentation » est la sortie vers la page complète,
 * avec le plan du manuel, pour qui veut explorer plutôt que dépanner.
 *
 * L'assistant du manuel se joint depuis le pied (#970) : la conversation prend
 * la place de l'article, et une citation **change l'article de la fenêtre** —
 * elle ne la ferme pas, puisque tout l'intérêt est de ne pas perdre son écran.
 *
 * Ouverture : `openDialog(DocumentationArticleDialogComponent,
 * 'DOCUMENTATION_DIALOG', { docId, docAnchor })`.
 */
@Component({
    selector: 'app-documentation-article-dialog',
    imports: [
        ButtonModule,
        DocumentationArticleViewComponent,
        DocumentationAssistantComponent,
    ],
    templateUrl: './documentation-article-dialog.component.html'
})
export class DocumentationArticleDialogComponent extends BaseComponent {

    /*----------------------------------------------------------------------------------------
     * Gestion de la vue
     ----------------------------------------------------------------------------------------*/

    @ViewChild(DocumentationArticleViewComponent) private _view?: DocumentationArticleViewComponent;

    private _config = inject(DynamicDialogConfig);
    private _ref = inject(DynamicDialogRef);

    /*----------------------------------------------------------------------------------------
     * Données
     ----------------------------------------------------------------------------------------*/

    private _anchor?: string;
    public get anchor(): string | undefined {
        return this._anchor;
    }

    private _article?: DocArticle;
    public get article(): DocArticle | undefined {
        return this._article;
    }

    /**
     * Ce que la question dira d'elle-même : l'article affiché dans la fenêtre.
     * Posé au chargement de l'article plutôt que calculé par un accesseur : il
     * est lié au gabarit, et un objet neuf à chaque cycle de détection rejouerait
     * l'`@Input` sans fin.
     */
    private _assistantContext: DocAssistantContext | null = null;
    public get assistantContext(): DocAssistantContext | null {
        return this._assistantContext;
    }

    private _articleId?: string;
    public get articleId(): string | undefined {
        return this._articleId;
    }

    /** La conversation a pris la place de l'article dans la fenêtre. */
    private _inConversation = false;
    public get inConversation(): boolean {
        return this._inConversation;
    }

    /** Identité de cette fenêtre, posée à l'ouverture — cf. `_applyArticle()`. */
    private _uuid: string = '';

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    constructor() {
        super();
        this._articleId = this._config.data?.docId;
        this._anchor = this._config.data?.docAnchor;
        this._uuid = this._config.data?.uuid ?? '';
    }

    public onClickAskAssistant(): void {
        this._inConversation = true;
    }

    public onClickBackToArticle(): void {
        this._inConversation = false;
    }

    public onClickOpenInDocumentation(): void {
        this._goToPage();
    }

    public onLoadArticle(article: DocArticle | undefined): void {
        this._applyArticle(article);
    }

    public onOpenArticle(target: DocLinkTarget): void {
        this._showArticle(target);
    }

    /** Citation de l'assistant : elle change l'article, et rend la fenêtre à la lecture. */
    public onOpenDoc(target: DocLinkTarget): void {
        this._inConversation = false;
        this._showArticle(target);
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    /**
     * La conversation est rattachée à **cette fenêtre-ci** — son `uuid`, posé à
     * l'ouverture — **et à l'article affiché**. Une fenêtre s'ouvre pour se
     * dépanner et se referme : elle n'hérite pas de ce qu'on lisait ailleurs, et
     * deux fenêtres successives ne partagent pas leur fil. Suivre une citation y
     * change d'article, donc de sujet : la conversation suivante repart, et
     * nomme le nouvel article.
     */
    private _applyArticle(article: DocArticle | undefined): void {
        this._article = article;
        this._assistantContext = article
            ? { kind: 'article', id: article.id, label: article.title, scope: `window:${this._uuid}:${article.id}` }
            : null;
    }

    /**
     * Le manuel s'ouvre dans un **nouvel onglet**, et non par une navigation :
     * la fenêtre a été appelée depuis un écran de travail, souvent une saisie en
     * cours. Quitter cet écran pour lire l'article ferait perdre ce qui n'est pas
     * enregistré — c'est précisément ce que la fenêtre évitait.
     */
    private _goToPage(): void {
        this._ref.close();
        const url = this.router.serializeUrl(
            this.router.createUrlTree([this.appService.clientUrl('/documentation'), this._articleId], this._anchor ? { fragment: this._anchor } : {})
        );
        window.open(url, '_blank');
    }

    /**
     * Un renvoi vers une section de l'article **déjà affiché** ne change pas
     * d'article : le corps n'est pas rechargé, et il n'y aurait donc rien à
     * voir (#983). C'est un défilement de la fenêtre, attendu d'un cycle — la
     * vue vient peut-être d'être rendue, la conversation lui ayant pris la
     * place.
     */
    private _showArticle(target: DocLinkTarget): void {
        this._anchor = target.anchor;
        if (target.id !== this._articleId) {
            this._articleId = target.id;
            return;
        }
        setTimeout(() => {
            if (this._anchor) {
                this._view?.scrollToAnchor(this._anchor);
            }
        }, 1);
    }
}
